import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAppContext } from "@/contexts/AppContext";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import DriverSummaryModal from "./DriverSummaryModal";
import { buildRateContext, computeDriverPortion, flatCommissionPercent, gstRateFraction, insuranceExcessAmount, type ResolvedRateContext } from "@/lib/paymentRates";
import { loadPayoutColumnMap, DEFAULT_PAYOUT_COLUMN_MAP, loadDeductionCategories, type DeductionCategory } from "@/lib/settingsConfig";
import { buildPayrollTxtFile, nextBatchId, type BankPayment } from "@/lib/anzBankFile";
import { recordWeeklyFundContributions } from "@/lib/incidentReports";
import { loadWeekDeductionLines, recordWeekDeductions, money as dollars, type DeductionLine } from "@/lib/driverDeductions";

const DEFAULT_TENANT_ID = "1225f4cb-43ae-463d-a6b0-53f721362dbf";

/* The turnover columns that exist on a weekly-payout job row. The column ->
   payment-type mapping itself is loaded from Settings (PayoutMapping) so a
   payment-type rename can't silently break commission. */
const PAYOUT_COLUMN_KEYS = [
  "cash_amt",
  "eftpos_amt",
  "totalmobility_amt",
  "tm_hoist_amt",
  "account_amt",
  "ecard_amt",
  "discount",
  "other_amt",
];


/* ---------- Date helpers ---------- */
const startOfWeekMon = (d: Date) => {
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  const nd = new Date(d);
  nd.setDate(d.getDate() + diff);
  nd.setHours(0, 0, 0, 0);
  return nd;
};
const endOfWeekSun = (d: Date) => {
  const s = startOfWeekMon(d);
  const e = new Date(s);
  e.setDate(s.getDate() + 6);
  e.setHours(23, 59, 59, 999);
  return e;
};
const addDays = (d: Date, n: number) => {
  const nd = new Date(d);
  nd.setDate(d.getDate() + n);
  return nd;
};
const fmtISODateLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/* ---------- Types ---------- */
type DriverRow = {
  driver_name: string;
  bank_account_number: string | null;
  bank_account_name: string | null;
  payout_reference_override: string | null;
};

type DcpRow = {
  driver_name: string;
  pu_date: string | null;
  reconciled: boolean | null;
  cash_amt: number | null;
  eftpos_amt: number | null;
  totalmobility_amt: number | null;
  tm_hoist_amt: number | null;
  account_amt: number | null;
  other_amt: number | null;
  ecard_amt: number | null;
  discount: number | null;
};

type DriverTotals = {
  driver_name: string;
  job_count: number;
  gross_turnover: number;
  gst_component: number;
  commission_amount: number;
  deductions: number;   // insurance + structured/manual deductions - credits
  bonus: number;        // from bonus tiers
  net_pay: number;
};

const currency = (n: number) => `$${n.toFixed(2)}`;

/* ---------- Component ---------- */
const WeeklyPayoutWorkspace: React.FC = () => {
  const { adminSettings, calculateBonus, effectivePermissions } = useAppContext() as any;
  // A restricted user (see src/lib/permissions.ts, Settings -> Team
  // Access) only ever sees their linked driver(s)' totals here -- this
  // whole-company weekly summary is the most sensitive view in this tab.
  // Unrestricted (the default -- everyone today) is unaffected.
  const scopedDriverNames = effectivePermissions?.isUnrestricted === false ? effectivePermissions.linkedDriverNames : null;
  const [weekStart, setWeekStart] = useState<Date>(() => startOfWeekMon(new Date()));
  const [weekEnd, setWeekEnd] = useState<Date>(() => endOfWeekSun(new Date()));
  const [datePaid, setDatePaid] = useState<string>("");

  const [drivers, setDrivers] = useState<DriverRow[]>([]);
  const [rows, setRows] = useState<DcpRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [selectAll, setSelectAll] = useState(false);

  /* housekeeping: bulk "mark as paid" cutoff. Defaults to the PREVIOUS Monday
     relative to the current week's Monday (e.g. if this week's Monday is
     25/05/2026 the default cutoff is 18/05/2026), so everything before last
     week is retired and the current + prior week stay live. */
  const [paidCutoff, setPaidCutoff] = useState<string>(() =>
    fmtISODateLocal(addDays(startOfWeekMon(new Date()), -7)),
  );
  const [housekeeping, setHousekeeping] = useState(false);


  /* modal control */
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDriver, setModalDriver] = useState<string>("");

  /* adjustments cache for the week */
  const [weekAdjustments, setWeekAdjustments] = useState<
    Record<string, { deductions: number; credits: number; insurance: number; excessRepayment: number }>
  >({});

  /* the week's deduction lines (what's recorded in the ledger for this week,
     or projected from each charge until it is) -- see src/lib/driverDeductions.ts */
  const [weekLines, setWeekLines] = useState<DeductionLine[]>([]);

  /* Reducing-balance charges whose balance has never been touched since the
     deduction ledger was introduced -- everything taken before then went
     unrecorded, so the balance still shows the full original amount. Worth
     confirming before the first export, or the charge will be collected for
     longer than it should. */
  const unconfirmedBalances = useMemo(() => {
    const ws = fmtISODateLocal(weekStart);
    return weekLines.filter(
      (l) =>
        l.kind === "deduction" &&
        l.mode === "reducing_balance" &&
        !l.recorded &&
        l.paidToDate !== null &&
        l.paidToDate === l.thisPay &&
        (l.adjustment.starts_on ?? "") < ws &&
        Number(l.adjustment.remaining_balance ?? 0) === Number(l.adjustment.total_amount ?? 0)
    );
  }, [weekLines, weekStart]);

  /* per-driver deductions that route to a separate bank account (Settings →
     Deduction Categories), so the banking file can pay them out as their own
     line instead of the amount just disappearing from net pay. */
  const [weekDeductionPayouts, setWeekDeductionPayouts] = useState<
    Record<string, { categoryKey: string; label: string; amount: number; accountNumber: string; accountName: string; reference: string }[]>
  >({});

  /* deduction categories (Settings → Deduction Categories), including which
     ones carry a bank account to redirect payouts to. */
  const [deductionCategories, setDeductionCategories] = useState<DeductionCategory[]>([]);
  useEffect(() => {
    loadDeductionCategories(DEFAULT_TENANT_ID).then(setDeductionCategories).catch(() => {});
  }, []);

  /* per-payment-type + per-driver rate context (DB-driven) */
  const [rateCtx, setRateCtx] = useState<ResolvedRateContext | null>(null);

  /* column -> payment-type mapping (DB-driven, from Settings → Payment Types) */
  const [columnMap, setColumnMap] = useState<Record<string, string | null>>(DEFAULT_PAYOUT_COLUMN_MAP);

  useEffect(() => {
    loadPayoutColumnMap(DEFAULT_TENANT_ID).then(setColumnMap).catch(() => {});
  }, []);


  /* ---------- Load drivers ---------- */
  useEffect(() => {
    (async () => {
      let query = supabase
        .from("drivers")
        .select("driver_name, bank_account_number, bank_account_name, payout_reference_override")
        .order("driver_name", { ascending: true });
      if (scopedDriverNames) query = query.in("driver_name", scopedDriverNames.length > 0 ? scopedDriverNames : ["__none__"]);
      const { data, error } = await query;
      if (error) {
        toast({ title: "Error loading drivers", description: error.message, variant: "destructive" });
      } else {
        setDrivers((data || []) as DriverRow[]);
      }
    })();
  }, [scopedDriverNames]);

  /* ---------- Deductions for the week ---------- */
  // Aggregates to driver-level STRICTLY from each driver's own deduction
  // lines. Insurance is NO LONGER auto-applied to every driver -- it only
  // appears for drivers who actually have an "insurance" adjustment
  // configured (so e.g. Daren, with no deductions set up, shows $0.00).
  // "insurance_excess_repayment" (a confirmed excess charge being repaid, see
  // Insurance -> Incidents) is tracked separately too, purely so the
  // Deductions column's breakdown tooltip can show it apart from the flat
  // weekly contribution -- it still adds into the same deductions total.
  //
  // The amounts come from loadWeekDeductionLines: what the ledger already
  // recorded for this pay week, or each charge's projected amount (capped at
  // what's still owed) until the week is exported.
  const loadDeductionData = async (ws: string, we: string) => {
    const categoryByKey: Record<string, DeductionCategory> = {};
    deductionCategories.forEach((c) => { categoryByKey[c.category_key] = c; });

    const lines = await loadWeekDeductionLines({
      weekStart: ws,
      weekEnd: we,
      insuranceWeekly: insuranceExcessAmount(adminSettings),
      labelFor: (key) =>
        (key && categoryByKey[key]?.label) ||
        (key ? key.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) : "Deduction"),
    });

    const map: Record<string, { deductions: number; credits: number; insurance: number; excessRepayment: number }> = {};

    // Deductions whose category (Settings → Deduction Categories) has a bank
    // account configured get their own entry here too, so the banking file
    // can pay that amount out separately instead of it just disappearing
    // from the driver's net pay.
    const redirects: Record<
      string,
      { categoryKey: string; label: string; amount: number; accountNumber: string; accountName: string; reference: string }[]
    > = {};

    lines.forEach((line) => {
      const a = line.adjustment;
      const effectiveAmount = line.thisPay;
      if (!map[line.driverName]) map[line.driverName] = { deductions: 0, credits: 0, insurance: 0, excessRepayment: 0 };
      const cat = categoryByKey[a.category ?? ""];

      if (line.kind === "insurance") {
        map[line.driverName].insurance += effectiveAmount;
      } else if (line.kind === "deduction") {
        map[line.driverName].deductions += effectiveAmount;
        if (a.category === "insurance_excess_repayment") map[line.driverName].excessRepayment += effectiveAmount;
      } else {
        map[line.driverName].credits += effectiveAmount;
      }

      if (line.kind === "credit" || effectiveAmount <= 0) return;
      if (!redirects[line.driverName]) redirects[line.driverName] = [];

      if (cat?.payout_mode === "company_wide" && cat.bank_account_number) {
        // Every driver under this category pays the SAME shared account
        // (e.g. the Excess Fund) -- sum same-category rows into one line.
        const existing = redirects[line.driverName].find((r) => r.categoryKey === a.category);
        if (existing) {
          existing.amount += effectiveAmount;
        } else {
          redirects[line.driverName].push({
            categoryKey: a.category ?? "",
            label: cat.label,
            amount: effectiveAmount,
            accountNumber: cat.bank_account_number,
            accountName: cat.bank_account_name || cat.label,
            reference: line.driverName,
          });
        }
      } else if (cat?.payout_mode === "individual" && a.bank_account_number) {
        // Each row has its own payee (e.g. a court fine) -- never merge,
        // since two rows in the same category can go to different accounts.
        redirects[line.driverName].push({
          categoryKey: a.category ?? "",
          label: cat.label,
          amount: effectiveAmount,
          accountNumber: a.bank_account_number,
          accountName: a.bank_account_name || line.driverName,
          reference: a.payment_reference || line.driverName,
        });
      }
    });

    setWeekLines(lines);
    setWeekAdjustments(map);
    setWeekDeductionPayouts(redirects);
  };

  /* A week is "being paid" when its bank file is exported -- that is when
     each driver's deductions for the week are written to the ledger, so a
     reducing balance really reduces. Idempotent per charge and week (re-
     exporting, or using both buttons, never deducts twice), and the database
     refuses to take more than is still owed. */
  const recordDeductionsFor = async (driverNames: string[], ws: string, we: string) => {
    try {
      const names = new Set(driverNames);
      const result = await recordWeekDeductions({
        weekStart: ws,
        weekEnd: we,
        txnDate: datePaid || we,
        lines: weekLines.filter((l) => names.has(l.driverName)),
      });
      if (result.capped.length) {
        toast({
          title: "Deduction capped at the balance owed",
          description: result.capped
            .map((c) => `${c.driver_name}: asked for ${dollars(c.requested)}, only ${dollars(c.applied)} was left`)
            .join("; "),
        });
      }
      if (result.completed.length) {
        toast({ title: "Charge paid off", description: `${result.completed.length} deduction${result.completed.length === 1 ? "" : "s"} reached $0.00 and ${result.completed.length === 1 ? "is" : "are"} now marked paid.` });
      }
      if (result.recorded > 0) await loadDeductionData(ws, we);
    } catch (e) {
      console.error("Error recording deductions:", e);
      toast({
        title: "Deductions weren't recorded",
        description: "The bank file was created, but this week's deductions couldn't be saved to the ledger. Use \"Record Deductions\" to try again.",
        variant: "destructive",
      });
    }
  };

  /* ---------- Load reconciled jobs via RPC + adjustments ---------- */
  const loadTotals = async () => {
    setLoading(true);
    try {
      const ws = fmtISODateLocal(weekStart);
      const we = fmtISODateLocal(weekEnd);

      // Ensure we have driver names
      let driverNames = drivers.map((d) => d.driver_name);
      if (!driverNames.length) {
        let fallbackQuery = supabase
          .from("drivers")
          .select("driver_name")
          .order("driver_name", { ascending: true });
        if (scopedDriverNames) fallbackQuery = fallbackQuery.in("driver_name", scopedDriverNames.length > 0 ? scopedDriverNames : ["__none__"]);
        const { data: d2, error: dErr } = await fallbackQuery;
        if (dErr) throw dErr;
        driverNames = (d2 || []).map((d: any) => d.driver_name);
        if (!drivers.length) setDrivers((d2 as any) || []);
      }

      // Load DB-driven per-payment-type + per-driver rate context for these drivers
      try {
        const ctx = await buildRateContext(driverNames);
        setRateCtx(ctx);
      } catch (rcErr) {
        console.error("Error loading rate context:", rcErr);
      }


      // Jobs (RLS-safe) — NEW RPC + NEW PARAM NAMES
      const { data, error } = await supabase.rpc("get_weekly_payout_jobs", {
        p_driver_names: driverNames,
        p_week_start: ws,
        p_week_end: we,
      });
      if (error) throw error;

      setRows(((Array.isArray(data) ? data : []) as any[]).map((r) => ({
        driver_name: r.driver_name,
        pu_date: r.pu_date,
        reconciled: r.reconciled,
        cash_amt: r.cash_amt,
        eftpos_amt: r.eftpos_amt,
        totalmobility_amt: r.totalmobility_amt,
        tm_hoist_amt: r.tm_hoist_amt,
        account_amt: r.account_amt,
        other_amt: r.other_amt,
        ecard_amt: r.ecard_amt,
        discount: r.discount,
      })));

      await loadDeductionData(ws, we);

      toast({
        title: "Summary loaded",
        description: `${(data as any[])?.length || 0} reconciled jobs found for ${ws} → ${we}`,
      });

      setSelected({});
      setSelectAll(false);
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Error loading jobs",
        description: err?.message ?? String(err),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Aggregate ---------- */
  const totals: DriverTotals[] = useMemo(() => {
    const COLS = PAYOUT_COLUMN_KEYS;
    const t: Record<string, DriverTotals> = {};
    // Per-driver, per-day, per-column turnover accumulator so we can apply the correct
    // payment-type / driver-override / lease rate to each portion of the turnover. It is
    // per day because a driver can change arrangement part-way through the week
    // (commission -> lease); the rate is picked by each job's date.
    const perDate: Record<string, Record<string, Record<string, number>>> = {};

    for (const r of rows) {
      const name = r.driver_name || "Unknown";
      if (!t[name]) {
        t[name] = {
          driver_name: name,
          job_count: 0,
          gross_turnover: 0,
          gst_component: 0,
          commission_amount: 0,
          deductions: 0,
          bonus: 0,
          net_pay: 0,
        };
        perDate[name] = {};
      }
      t[name].job_count += 1;
      const dateKey = String((r as any).pu_date || "undated").slice(0, 10);
      const dayCols = (perDate[name][dateKey] ||= {});
      let sum = 0;
      COLS.forEach((col) => {
        const v = Number((r as any)[col] || 0);
        dayCols[col] = (dayCols[col] || 0) + v;
        sum += v;
      });
      t[name].gross_turnover += sum;
    }

    // Company defaults come from one source (Settings → fallback) so the
    // payout report, driver summary modal and payment modal never disagree.
    const gstRate = gstRateFraction(adminSettings);
    const flatCommission = flatCommissionPercent(adminSettings);

    const weekEndIso = fmtISODateLocal(weekEnd);

    Object.values(t).forEach((d) => {

      // The DB-driven "% per payment type" is the DRIVER-PAYABLE share — i.e.
      // the portion of (ex-GST) turnover the driver keeps. So a HIGHER % means
      // the driver gets MORE, and the company's retained commission is the
      // remainder. (Previously the % was wrongly treated as the company's cut,
      // which inverted the payout: dropping the % paid the driver more.)
      const { driverPortion, bonusEligibleGross, gstAmount, netTake } = computeDriverPortion({
        ctx: rateCtx,
        driverName: d.driver_name,
        byDate: perDate[d.driver_name] || {},
        ptNameFor: (col) => columnMap[col],
        gstRate,
        flatCommission,
        fallbackDate: weekEndIso,
      });
      // "Commission" displayed = the company's retained cut (ex-GST take minus
      // what the driver is paid). Net pay to the driver = their payable share.
      d.gst_component = gstAmount;
      d.commission_amount = Math.max(0, netTake - driverPortion);

      // Bonus based on GROSS turnover -- matches the "(based on gross)"
      // label on the driver summary/payslip and the round-number tier
      // thresholds in Bonus Tier Management. (Previously ran against exGst,
      // which understated a driver's tier relative to what the screen said.)
      // Days on an arrangement with bonus tiers off (lease) are left out.
      try {
        d.bonus = typeof calculateBonus === "function" ? Number(calculateBonus(bonusEligibleGross) || 0) : 0;
      } catch {
        d.bonus = 0;
      }

      // deductions from map (insurance + structured/manual – credits)
      const adj = weekAdjustments[d.driver_name] || { deductions: 0, credits: 0, insurance: 0, excessRepayment: 0 };
      d.deductions = Number(adj.insurance || 0) + Number(adj.deductions || 0) - Number(adj.credits || 0);

      // Net = driver-payable share + Bonus – Deductions
      d.net_pay = Math.max(0, driverPortion + d.bonus - d.deductions);

    });

    return Object.values(t).sort((a, b) => a.driver_name.localeCompare(b.driver_name));
  }, [rows, adminSettings, weekAdjustments, rateCtx, columnMap, calculateBonus, weekEnd]);


  /* ---------- UI helpers ---------- */
  const toggleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    const map: Record<string, boolean> = {};
    totals.forEach((d) => (map[d.driver_name] = checked));
    setSelected(map);
  };
  const toggleDriver = (name: string, checked: boolean) =>
    setSelected((prev) => ({ ...prev, [name]: checked }));

  /* ---------- Banking file export ---------- */
  // Build a CSV banking file for the loaded week. If any rows are ticked we
  // export just those drivers, otherwise the whole loaded list. Each line pairs
  // the driver's saved bank details (Driver Management → Payments) with their
  // net pay for the week — this is the "banking file" you can import into your
  // bank's bulk-payment / batch upload.
  const downloadBankingFile = async () => {
    if (!totals.length) {
      toast({ title: "Load a week first", description: "Click Load Summary before exporting a banking file.", variant: "destructive" });
      return;
    }
    const bankByName: Record<string, DriverRow> = {};
    drivers.forEach((d) => { bankByName[d.driver_name] = d; });

    const anySelected = Object.values(selected).some(Boolean);
    const chosen = totals.filter((t) => (anySelected ? selected[t.driver_name] : t.net_pay > 0));

    if (!chosen.length) {
      toast({ title: "Nothing to export", description: "No drivers selected with a payable amount.", variant: "destructive" });
      return;
    }

    const ws = fmtISODateLocal(weekStart);
    const we = fmtISODateLocal(weekEnd);
    const paid = datePaid || we;
    const header = ["Account Name", "Account Number", "Amount", "Particulars", "Code", "Reference"];
    const esc = (v: any) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const missing: string[] = [];
    const lines = chosen.flatMap((t) => {
      const b = bankByName[t.driver_name];
      if (!b?.bank_account_number) missing.push(t.driver_name);
      const ref = b?.payout_reference_override || `${ws}`;
      const driverLine = [
        b?.bank_account_name || t.driver_name,
        b?.bank_account_number || "",
        t.net_pay.toFixed(2),
        "Wages",
        ref,
        `Pay ${ws}`,
      ].map(esc).join(",");

      // Deductions routed to a separate bank account (Settings → Deduction
      // Categories) get their own line, named with the deduction itself in
      // Particulars. Reference is the driver's name for a company-wide
      // account (several drivers share it, so the name is what lets it
      // reconcile whose amount is whose) or the deduction's own reference/
      // case number for an individual account (e.g. a court fine).
      const redirectLines = (weekDeductionPayouts[t.driver_name] || []).map((r) =>
        [r.accountName, r.accountNumber, r.amount.toFixed(2), r.label, ref, r.reference].map(esc).join(",")
      );

      return [driverLine, ...redirectLines];
    });

    const total =
      chosen.reduce((s, t) => s + t.net_pay, 0) +
      chosen.reduce((s, t) => s + (weekDeductionPayouts[t.driver_name] || []).reduce((rs, r) => rs + r.amount, 0), 0);
    const csv = [
      `# Banking file — week ${ws} to ${we}, paid ${paid}`,
      `# ${lines.length} payments, total $${total.toFixed(2)}`,
      header.join(","),
      ...lines,
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `banking-file_${ws}_to_${we}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    if (missing.length) {
      toast({
        title: "Banking file exported (with gaps)",
        description: `Missing bank account number for: ${missing.join(", ")}. Add it in Driver Management → Payments.`,
        variant: "destructive",
      });
    } else {
      toast({ title: "Banking file exported", description: `${lines.length} payments, total $${total.toFixed(2)}.` });
    }

    // Generating a payment file is the real "this week is being paid" moment
    // -- log each paid driver's weekly excess-fund contribution now, keyed
    // per-week so re-exporting (or using both export buttons) never double-counts.
    try {
      await recordWeeklyFundContributions(
        chosen.map((t) => ({ driverName: t.driver_name, amount: weekAdjustments[t.driver_name]?.insurance || 0 })),
        ws
      );
    } catch (e) {
      console.error("Error recording weekly excess fund contributions:", e);
    }

    await recordDeductionsFor(chosen.map((t) => t.driver_name), ws, we);
  };

  /* ---------- Bank TXT export (NZ payroll comma-delimited) ---------- */
  // Generates the payroll-compatible, COMMA-DELIMITED NZ electronic-payments TXT
  // file (the same format used for ANZ uploads) from the loaded Weekly Payout
  // Summary rows. Same selection rule as the CSV banking file: ticked drivers if
  // any are ticked, otherwise everyone with a positive Net.
  //
  //   Header  : 1,,,,,,YYYYMMDD,YYYYMMDD,
  //   Payment : 2,{account_digits},52,{cents},{bank_account_name},,,,{particulars},{reference},,,{batch_id}
  //   Trailer : 3,{total_cents},{count},{hash_total}
  //
  // particulars = driver.payout_reference_override (where available),
  // reference   = payer reference (defaults to "THE GOLD & BLACK GRO",
  //               overridable via Settings), and batch_id auto-increments
  //               (EP000090, EP000091, …) and is persisted in localStorage.
  const downloadBankTxt = async () => {
    if (!totals.length) {
      toast({ title: "Load a week first", description: "Click Load Summary before exporting a bank TXT file.", variant: "destructive" });
      return;
    }
    const bankByName: Record<string, DriverRow> = {};
    drivers.forEach((d) => { bankByName[d.driver_name] = d; });

    const anySelected = Object.values(selected).some(Boolean);
    const chosen = totals.filter((t) => (anySelected ? selected[t.driver_name] : t.net_pay > 0));
    if (!chosen.length) {
      toast({ title: "Nothing to export", description: "No drivers selected with a payable amount.", variant: "destructive" });
      return;
    }

    const ws = fmtISODateLocal(weekStart);
    const we = fmtISODateLocal(weekEnd);
    const dueDate = datePaid ? new Date(datePaid) : weekEnd;

    // Payer reference shown on every payment row. Defaults to the agreed
    // "THE GOLD & BLACK GRO" but can be overridden from Settings if configured.
    const payerReference =
      adminSettings?.payer_reference ||
      adminSettings?.bank_payer_reference ||
      "THE GOLD & BLACK GRO";

    // Batch id auto-increments and persists across exports (EP000090 → EP000091…).
    const lastBatchId =
      (typeof window !== "undefined" && window.localStorage.getItem("payrollBatchId")) || "EP000089";
    const batchId = nextBatchId(lastBatchId);

    const payments: BankPayment[] = [];
    chosen.forEach((t) => {
      const b = bankByName[t.driver_name];
      payments.push({
        accountName: b?.bank_account_name || t.driver_name,
        accountNumber: b?.bank_account_number || "",
        amount: t.net_pay,
        // particulars = the driver's saved payout reference override where set.
        particulars: b?.payout_reference_override || "",
      });

      // Deductions routed to a separate bank account (Settings → Deduction
      // Categories) get their own payment row. This TXT format only has one
      // free-text field per row (particulars) -- it carries the driver's name
      // for a company-wide account (several drivers share it, so the name is
      // what identifies whose payment it is) or the deduction's own
      // reference/case number for an individual account (e.g. a court fine,
      // which the receiving payee needs to reconcile, not the driver's name).
      (weekDeductionPayouts[t.driver_name] || []).forEach((r) => {
        payments.push({
          accountName: r.accountName,
          accountNumber: r.accountNumber,
          amount: r.amount,
          particulars: r.reference,
        });
      });
    });

    const result = buildPayrollTxtFile(payments, {
      dueDate,
      payerReference,
      batchId,
    });

    if (!result.count) {
      toast({
        title: "No valid payments",
        description: "Every selected driver is missing a usable bank account number. Add them in Driver Management → Payments.",
        variant: "destructive",
      });
      return;
    }

    // Persist the batch id only once we know a real file was produced.
    if (typeof window !== "undefined") {
      window.localStorage.setItem("payrollBatchId", result.batchId);
    }

    const blob = new Blob([result.text], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payroll-bank_${ws}_to_${we}.TXT`;
    a.click();
    URL.revokeObjectURL(url);

    if (result.invalid.length) {
      toast({
        title: "Bank TXT exported (with gaps)",
        description: `${result.count} payment(s), total $${(result.totalCents / 100).toFixed(2)}, batch ${result.batchId}. Skipped (missing/invalid account): ${result.invalid.join(", ")}.`,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Bank TXT exported",
        description: `${result.count} payment(s), total $${(result.totalCents / 100).toFixed(2)}, batch ${result.batchId}.`,
      });
    }

    // Same "this week is actually being paid" hook as the CSV export -- only
    // for drivers who actually made it into the file (not skipped for a
    // missing/invalid bank account), and idempotent per week either way.
    const invalidNames = new Set(result.invalid);
    try {
      await recordWeeklyFundContributions(
        chosen
          .filter((t) => !invalidNames.has(t.driver_name))
          .map((t) => ({ driverName: t.driver_name, amount: weekAdjustments[t.driver_name]?.insurance || 0 })),
        ws
      );
    } catch (e) {
      console.error("Error recording weekly excess fund contributions:", e);
    }

    await recordDeductionsFor(
      chosen.filter((t) => !invalidNames.has(t.driver_name)).map((t) => t.driver_name),
      ws,
      we
    );
  };

  /* Manual fallback for a week that was paid some other way than a bank
     file here -- same idempotent recording, for the ticked drivers (or
     everyone loaded if none are ticked). */
  const recordDeductionsManually = async () => {
    if (!totals.length) return;
    const anySelected = Object.values(selected).some(Boolean);
    const names = totals.filter((t) => (anySelected ? selected[t.driver_name] : true)).map((t) => t.driver_name);
    const ws = fmtISODateLocal(weekStart);
    const we = fmtISODateLocal(weekEnd);
    const pending = weekLines.filter((l) => names.includes(l.driverName) && l.kind === "deduction" && !l.recorded);
    if (!pending.length) {
      toast({ title: "Nothing to record", description: "This week's deductions for those drivers are already recorded." });
      return;
    }
    if (!window.confirm(`Record ${pending.length} deduction${pending.length === 1 ? "" : "s"} for ${names.length} driver${names.length === 1 ? "" : "s"} against the week of ${ws}? This reduces their balances.`)) return;
    await recordDeductionsFor(names, ws, we);
    toast({ title: "Deductions recorded", description: `Week of ${ws}.` });
  };


  /* ---------- Housekeeping: mark prior jobs as paid ---------- */
  // Flags every job with pu_date strictly before the cutoff as paid so only
  // current data remains in play. This runs through the mark_jobs_paid_before
  // SQL function (RPC) rather than a direct table update — that avoids the
  // PostgREST "column does not exist" schema-cache error you were hitting, and
  // performs the update server-side in one statement.
  const markPriorPaid = async () => {
    if (!paidCutoff) return;
    const ok = window.confirm(
      `Mark ALL jobs before ${paidCutoff} as paid?\n\nThis sets driver_paid = true on driver_completed_payments and cannot be undone from here.`
    );
    if (!ok) return;
    setHousekeeping(true);
    try {
      const { data, error } = await supabase.rpc("mark_jobs_paid_before", {
        p_cutoff: paidCutoff,
      });

      if (error) {
        toast({
          title: "Couldn't mark as paid",
          description: error.message,
          variant: "destructive",
        });
      } else {
        const count = typeof data === "number" ? data : Number(data ?? 0);
        toast({
          title: "Prior jobs marked as paid",
          description: `${count} job(s) before ${paidCutoff} flagged driver_paid = true.`,
        });
        // Refresh the loaded week so the list reflects the change immediately.
        if (totals.length) loadTotals();
      }
    } catch (err: any) {
      toast({
        title: "Couldn't mark as paid",
        description: err?.message ?? String(err),
        variant: "destructive",
      });
    } finally {
      setHousekeeping(false);
    }
  };



  /* ---------- Week controls ---------- */
  const weekNav = (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        onClick={() => {
          const prev = addDays(weekStart, -7);
          setWeekStart(startOfWeekMon(prev));
          setWeekEnd(endOfWeekSun(prev));
        }}
      >
        ‹
      </Button>
      <Input
        type="date"
        value={fmtISODateLocal(weekStart)}
        onChange={(e) => {
          const picked = new Date(e.target.value);
          setWeekStart(startOfWeekMon(picked));
          setWeekEnd(endOfWeekSun(picked));
        }}
        className="w-40 text-center"
      />
      <Button
        variant="outline"
        onClick={() => {
          const next = addDays(weekStart, 7);
          setWeekStart(startOfWeekMon(next));
          setWeekEnd(endOfWeekSun(next));
        }}
      >
        ›
      </Button>
    </div>
  );

  /* ---------- Render ---------- */
  return (
    <div className="space-y-4">
      <Card className="shadow-lg bg-card border border-border">
        <CardHeader>
          <CardTitle className="text-xl text-primary">Weekly Payout Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3 items-end flex-wrap">
            <div>
              <Label>Select Week</Label>
              {weekNav}
            </div>
            <div>
              <Label>Date Paid</Label>
              <Input type="date" value={datePaid} onChange={(e) => setDatePaid(e.target.value)} />
            </div>
            <Button onClick={loadTotals} disabled={loading} className="golden-button">
              {loading ? "Loading..." : "Load Summary"}
            </Button>
            <Button variant="outline" onClick={downloadBankingFile} disabled={!totals.length}>
              Download Banking File
            </Button>
            <Button variant="outline" onClick={downloadBankTxt} disabled={!totals.length}>
              Export Bank TXT
            </Button>
            <Button variant="outline" onClick={recordDeductionsManually} disabled={!totals.length}>
              Record Deductions
            </Button>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">
            The banking file is built here: tick the drivers to pay (or leave all unticked to
            include everyone with a positive Net). <strong>Download Banking File</strong> exports a
            CSV; <strong>Export Bank TXT</strong> generates the payroll-compatible, comma-delimited
            NZ electronic-payments TXT (header <code>1,…,YYYYMMDD,YYYYMMDD,</code>, one type-2 payment
            row per driver — account digits, code 52, amount in cents, bank account name, particulars
            from the payout reference override and the "THE GOLD &amp; BLACK GRO" reference — plus a
            type-3 trailer with total cents, transaction count and hash total). The batch id
            auto-increments (e.g. EP000090) and the file saves with a .TXT extension, ready for your
            bank's bulk upload. Exporting either file also <strong>records that week's deductions</strong> for
            the drivers in it, so reducing-balance charges actually go down (it never deducts the same week
            twice). <strong>Record Deductions</strong> does the same without a file, for a week paid another way.
          </p>


          {/* Housekeeping: mark prior jobs as paid so only current data remains */}
          <div className="flex flex-wrap items-end gap-3 rounded border border-border bg-muted/30 p-3">
            <div>
              <Label>Mark jobs paid before</Label>
              <Input
                type="date"
                value={paidCutoff}
                onChange={(e) => setPaidCutoff(e.target.value)}
                className="w-44"
              />
            </div>
            <Button variant="secondary" onClick={markPriorPaid} disabled={housekeeping}>
              {housekeeping ? "Working…" : "Mark prior jobs as paid"}
            </Button>
            <p className="text-xs text-muted-foreground flex-1 min-w-[200px]">
              Sets <code>driver_paid = true</code> on every job dated before the cutoff so you're only
              working with current data. Defaults to last week's Monday (the Monday before this week's).
            </p>
          </div>


          {unconfirmedBalances.length > 0 && (
            <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
              <strong>Check these balances before exporting.</strong> Deductions taken before the deduction ledger
              was introduced weren't recorded, so these still show their full original amount:{" "}
              {unconfirmedBalances.map((l) => `${l.driverName} (${l.label})`).join("; ")}. Correct each one in{" "}
              <strong>Payout Report → Deductions Report → Correct balance</strong>, otherwise it will be collected for
              longer than it should.
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Checkbox checked={selectAll} onCheckedChange={(v) => toggleSelectAll(!!v)} />
                </TableHead>
                <TableHead>Driver</TableHead>
                <TableHead>Jobs</TableHead>
                <TableHead>Turnover</TableHead>
                <TableHead>GST</TableHead>
                <TableHead>Commission</TableHead>
                <TableHead>Deductions</TableHead>
                <TableHead>Bonus</TableHead>
                <TableHead>Net</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {totals.map((t) => (
                <TableRow key={t.driver_name}>
                  <TableCell>
                    <Checkbox
                      checked={!!selected[t.driver_name]}
                      onCheckedChange={(v) => toggleDriver(t.driver_name, !!v)}
                    />
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => {
                        setModalDriver(t.driver_name);
                        setModalOpen(true);
                      }}
                      className="text-blue-500 hover:underline"
                    >
                      {t.driver_name}
                    </button>
                  </TableCell>
                  <TableCell>{t.job_count}</TableCell>
                  <TableCell>{currency(t.gross_turnover)}</TableCell>
                  <TableCell>{currency(t.gst_component)}</TableCell>
                  <TableCell>{currency(t.commission_amount)}</TableCell>
                  <TableCell
                    title={(() => {
                      const adj = weekAdjustments[t.driver_name];
                      if (!adj) return undefined;
                      const other = Math.max(0, adj.deductions - adj.excessRepayment);
                      const parts = [
                        adj.insurance > 0 && `Insurance contribution: ${currency(adj.insurance)}`,
                        adj.excessRepayment > 0 && `Excess repayment: ${currency(adj.excessRepayment)}`,
                        other > 0 && `Other deductions: ${currency(other)}`,
                        adj.credits > 0 && `Credits: -${currency(adj.credits)}`,
                        ...weekLines
                          .filter((l) => l.driverName === t.driver_name && l.detail)
                          .map((l) => `${l.label}: this pay ${dollars(l.thisPay)} · ${l.detail}${l.recorded ? " (recorded)" : ""}`),
                      ].filter(Boolean);
                      return parts.length ? parts.join("\n") : undefined;
                    })()}
                  >
                    {currency(t.deductions)}
                  </TableCell>
                  <TableCell>{currency(t.bonus)}</TableCell>
                  <TableCell className="font-bold text-green-500">{currency(t.net_pay)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!totals.length && (
            <div className="text-sm text-muted-foreground">Load a week to see driver totals.</div>
          )}
        </CardContent>
      </Card>

      {/* Driver Summary Modal */}
      <DriverSummaryModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        driverName={modalDriver}
        weekStart={fmtISODateLocal(weekStart)}
        weekEnd={fmtISODateLocal(weekEnd)}
      />
    </div>
  );
};

export default WeeklyPayoutWorkspace;
